import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useIntl} from "react-intl";

// Components
import {Button, Form, Icons, InputNumeric, Loading, Select, Switch, Tooltip} from "@gpstrackit/gtc-react-controls";
import {NotificationManager} from 'Components/Notifications';
import {Card, CardContent, CardHeader} from '@mui/material';

// Helpers
import {convertToKPH} from "Core/data/Helpers";

// Styles
import './style.scss';

// Actions
import {resetCoachingEducation, updateDriverCaseAccountConfig} from './redux/slice';

const CoachingEducationWidget = ({ configData }) => {
    const dispatch = useDispatch();
    const {messages, locale} = useIntl();
    const formRef = Form.useForm();

    // Redux States
    const {update, loadingUpdate} = useSelector((state) => state.coachingEducationRedux);
    const user = useSelector((state) => state.securityRedux.user);

    // Local States
    const [data, setData] = useState({includeHighlight: false, isEnabled: false});
    const [milesOptions, setMilesOptions] = useState([]);
    const [enableSend, setEnableSend] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Constants
    const RISK_SCORE_GREATER = [
        {id: 10},
        {id: 20},
        {id: 30},
        {id: 40},
        {id: 50},
        {id: 60},
        {id: 70},
        {id: 80},
        {id: 90}
    ];
    const LIMIT_OPTIONS_BY = [
        {id: 0},
        {id: 7},
        {id: 15},
        {id: 30},
        {id: 60}
    ];
    const LIMIT_OPTIONS_EVENT = [
        {id: 1, label: "1"},
        {id: 2, label: "2"},
        {id: 3, label: "3"},
        {id: 4, label: "4"},
        {id: 5, label: "5"},
        {id: -99, label: messages['driver_performance_cases_all']}
    ];
    const EVENT_CATEGORIES = [
        {id: 1, label: '1'},
        {id: 2, label: '2'},
        {id: 3, label: '3'},
        {id: 4, label: '4'},
        {id: 5, label: '5'},
        {id: -99, label: messages['driver_performance_cases_all']}
    ];

    // Functions
    const validateFields = () => {
        if (!data?.isEnabled) {
            return true;
        }
        const validateFields = ['driverScope', 'limitedBy', 'eventCategories', 'eventsLimit', 'frequencyDays'];
        return validateFields.every(field => data[field]);
    }

    const convertToKph = (value) => {
        return {id: value, label: user.isKph ? `${convertToKPH(value)} ${messages['kilometers']}` : `${value} ${messages['miles']}`}
    }

    const handleSave = async () => {
        let payload = {
            config: {
                isEnabled: data?.isEnabled || false,
                frequencyDays: Math.max(0, parseInt(data?.frequencyDays) || 0),
                driverScope: data?.driverScope?.id || 0,
                limitedBy: data?.limitedBy?.id || 0,
                eventCategories: data?.eventCategories?.id || 0,
                eventsLimit: data?.eventsLimit?.id || 0,
                includeHighlight: data?.includeHighlight || false,
                riskScoreGreaterThan: Math.max(10, Math.min(90, data?.riskScoreGreaterThan?.id || 50)),
                highSeverityEvents: data?.highSeverityEvents || false,
                mediumSeverityEvents: data?.mediumSeverityEvents || false
            }
        }
        try {
            await dispatch(updateDriverCaseAccountConfig(payload));
        } finally {
            setIsSubmitting(false);
        }
    }

    // Effects
    useEffect(() => {
        if (user) {
            setMilesOptions([
                convertToKph(50),
                convertToKph(100),
                convertToKph(150),
                convertToKph(200),
                {id: 0, label: 'NA'}
            ]);
        }
    }, [user, locale])



    useEffect(() => {
        if (data) {
            setEnableSend(validateFields());
        }
    }, [data])

    useEffect(() => {
        if (update) {
            if (update?.success) {
                NotificationManager.success(
                    messages['success'],
                    messages['driverPerformanceFunctionTab_coachingEducation_updatedConfiguration'],
                    3000,
                );
                setIsEditable(false)
            } else {
                const errorMessage = update?.error || messages['driverPerformanceFunctionTab_coachingEducation_updatedConfigurationError'];
                NotificationManager.error(
                    messages['error'],
                    errorMessage,
                    5000,
                );
            }
            dispatch(resetCoachingEducation({update: null}));
        }
    }, [update])

    useEffect(() => {
        if (configData && configData?.jsonValues) {
            try {
                const config = JSON.parse(configData.jsonValues);

                setData({
                    ...data,
                    isEnabled: configData?.configEnable === 1,
                    frequencyDays: config?.frequencyDays || 1,
                    includeHighlight: config?.includeHighlight || false,
                    riskScoreGreaterThan: {id :config?.riskScoreGreaterThan || 50},
                    highSeverityEvents: config?.highSeverityEvents || false,
                    mediumSeverityEvents: config?.mediumSeverityEvents || false,
                    driverScope: milesOptions.find(opt => opt.id === config?.driverScope) || null,
                    limitedBy: LIMIT_OPTIONS_BY.find(opt => opt.id === config?.limitedBy) || null,
                    eventsLimit: LIMIT_OPTIONS_EVENT.find(opt => opt.id === config?.eventsLimit) || null,
                    eventCategories: EVENT_CATEGORIES.find(opt => opt.id === config?.eventCategories) || null,
                })
            } catch (error) {
                console.error('Error parsing coaching education config:', error);
            }
        }
    }, [configData, milesOptions])

    return (
        <Card className="widget-paper" style={{ position: 'relative' }}>
            {(loadingUpdate) && <Loading overlay show style={{ position: 'absolute' }} />}
            <CardHeader
                title={
                    <div className="ce-header-content">
                        <div className="header-title">{messages['driverPerformanceFunctionTab_coachingEducation_coachingEducation']}</div>
                        <div className="header-row">
                            <Switch
                                checked={data?.isEnabled || false}
                                id="isEnabled"
                                label={messages['driverPerformanceFunctionTab_coachingEducation_autoCurateCoachingCases']}
                                labelPosition="left"
                                name="isEnabled"
                                onChange={(value) => {
                                    setData({...data, isEnabled: value});
                                    setIsEditable(true);
                                }}
                                size="small"
                            />
                        </div>
                    </div>
                }
            />
            <CardContent>
                <Form
                    ref={formRef.ref}
                    loading={loadingUpdate || isSubmitting}
                    onValidSubmit={async () => {
                        if (validateFields()) {
                            setIsSubmitting(true);
                            await handleSave();
                        }
                    }}
                    onChangeValues={() => { }}
                    className="coaching_education"
                >
                    {/* Configuration Section */}
                    <div className="function-panel-section">

                        <div className="function-panel-field">
                            <label className="label-gtc-rc">{messages['driverPerformanceFunctionTab_coachingEducation_frequencyDays']}<span className="text-danger function-panel-info-strong-required"> *</span></label>
                            <div className="frequency-input-wrapper">
                                <InputNumeric
                                    id="frequencyDays"
                                    value={data.frequencyDays}
                                    onChange={(value) => {
                                        if (value === '' || value === null || value === undefined) {
                                            setData({...data, frequencyDays: ''});
                                            return;
                                        }

                                        const numValue = Number.parseInt(value);

                                        if (Number.isNaN(numValue)) {
                                            setData({...data, frequencyDays: ''});
                                            return;
                                        }

                                        if (numValue >= 1 && numValue <= 60) {
                                            setData({...data, frequencyDays: numValue});
                                        } else if (numValue > 60) {
                                            setData({...data, frequencyDays: 60});
                                        } else if (numValue < 1) {
                                            setData({...data, frequencyDays: 1});
                                        }
                                        setIsEditable(true);
                                    }}
                                    onBlur={() => {
                                        if (!data.frequencyDays || data.frequencyDays === '') {
                                            setData({...data, frequencyDays: 1});
                                        }
                                    }}
                                    placeholder={messages['driverPerformanceFunctionTab_coachingEducation_frequencyDays']}
                                    min={1}
                                    max={60}
                                    isInteger={true}
                                    className="frequency-input"
                                    isDisabled={!data?.isEnabled}
                                    required
                                />
                                <div className="frequency-arrows">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newValue = (Number.parseInt(data.frequencyDays) || 0) + 1;
                                            if (newValue <= 60) {
                                                setData({...data, frequencyDays: newValue});
                                                setIsEditable(true);
                                            }
                                        }}
                                        className="arrow-button"
                                        disabled={data.frequencyDays >= 60 || !data?.isEnabled}
                                    >
                                        ▲
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newValue = (parseInt(data.frequencyDays) || 0) - 1;
                                            if (newValue >= 1) {
                                                setData({...data, frequencyDays: newValue});
                                                setIsEditable(true);
                                            }
                                        }}
                                        className="arrow-button"
                                        disabled={data.frequencyDays <= 1 || !data?.isEnabled}
                                    >
                                        ▼
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Driver Scope Section */}
                    <div className="function-panel-section">
                        <h4 className="function-panel-section-header">{messages['driver_performance_cases_create_driverScope']}</h4>

                        <div className="function-panel-field">
                            <strong className="function-panel-info-strong">
                                {messages['driverPerformanceFunctionTab_coachingEducation_riskScoreGreaterThan']}
                                <span className="text-danger function-panel-info-strong-required"> *</span>
                            </strong>
                            <Select
                                id="riskScoreGreaterThan"
                                value={data.riskScoreGreaterThan}
                                onChange={(value) => {
                                    setData({...data, riskScoreGreaterThan: value});
                                    setIsEditable(true);
                                }}
                                options={RISK_SCORE_GREATER}
                                textField="id"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={false}
                                isDisabled={!data?.isEnabled}
                            />
                        </div>
                        <div className="function-panel-field">
                            <strong className="function-panel-info-strong">
                                {user.isKph ? messages['driver_performance_cases_create_kilometersDriven'] : messages['driver_performance_cases_create_milesDriven']}
                                <span className="text-danger function-panel-info-strong-required"> *</span>
                                <Tooltip
                                    className="function-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_milesDrivenDetail']}>
                                    <Icons name="help" size="small"/>
                                </Tooltip>
                            </strong>
                            <Select
                                id="driverScope"
                                value={data.driverScope}
                                onChange={(value) => {
                                    setData({...data, driverScope: value});
                                    setIsEditable(true);
                                }}
                                options={milesOptions}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={false}
                                isDisabled={!data?.isEnabled}
                            />
                        </div>
                    </div>

                    {/* Limited By Section */}
                    <div className="function-panel-section">
                        <h4 className="function-panel-section-header">{messages['driver_performance_cases_create_limitedBy']}</h4>

                        <div className="function-panel-field">
                            <strong className="function-panel-info-strong">
                                {messages['driver_performance_cases_create_lastCoached']}
                                <span className="text-danger function-panel-info-strong-required"> *</span>
                                <Tooltip
                                    className="function-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_lastCoachedDetail']}>
                                    <Icons name="help" size="small"/>
                                </Tooltip>
                            </strong>
                            <Select
                                id="limitedBy"
                                value={data.limitedBy}
                                onChange={(value) => {
                                    setData({...data, limitedBy: value});
                                    setIsEditable(true);
                                }}
                                options={LIMIT_OPTIONS_BY}
                                textField="id"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={false}
                                isDisabled={!data?.isEnabled}
                            />
                        </div>
                    </div>

                    {/* Event Types to Include Section */}
                    <div className="function-panel-section">
                        <h4 className="function-panel-section-header">{messages['driver_performance_cases_create_eventTypesToInclude']}</h4>

                        <div className="function-panel-switch-container">
                            <Switch
                                checked={data?.includeHighlight || false}
                                id="includeHighlight"
                                label={messages['driver_performance_cases_create_eventStatusQueueForCoaching']}
                                labelPosition="top"
                                name="includeHighlight"
                                onChange={(value) => {
                                    setData({...data, includeHighlight: value});
                                    setIsEditable(true);
                                }}
                                size="small"
                                isDisabled={!data?.isEnabled}
                            />
                            <div className="function-panel-tooltip-switch function-tab-includeHighlight">
                                <Tooltip
                                    placement="right"
                                    title={messages['driver_performance_cases_create_eventStatusQueueForCoachingDetail']}>
                                    <Icons name="help" size="small"/>
                                </Tooltip>
                            </div>
                        </div>

                        <div className="function-panel-switch-container">
                            <Switch
                                checked={data?.highSeverityEvents || false}
                                id="highSeverityEvents"
                                label={messages['driverPerformanceFunctionTab_coachingEducation_highSeverityEvents']}
                                labelPosition="top"
                                name="highSeverityEvents"
                                onChange={(value) => {
                                    setData({...data, highSeverityEvents: value});
                                    setIsEditable(true);
                                }}
                                size="small"
                                isDisabled={!data?.isEnabled}
                            />
                        </div>

                        <div className="function-panel-switch-container">
                            <Switch
                                checked={data?.mediumSeverityEvents || false}
                                id="mediumSeverityEvents"
                                label={messages['driverPerformanceFunctionTab_coachingEducation_mediumSeverityEvents']}
                                labelPosition="top"
                                name="mediumSeverityEvents"
                                onChange={(value) => {
                                    setData({...data, mediumSeverityEvents: value});
                                    setIsEditable(true);
                                }}
                                size="small"
                                isDisabled={!data?.isEnabled}
                            />
                        </div>

                        <div className="function-panel-field">
                            <strong className="function-panel-info-strong">
                                {messages['driver_performance_cases_create_byFrequency']}
                                <span className="text-danger function-panel-info-strong-required"> *</span>
                                <Tooltip
                                    className="function-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_byFrequencyDetail']}>
                                    <Icons name="help" size="small"/>
                                </Tooltip>
                            </strong>
                            <Select
                                id="eventCategories"
                                value={data.eventCategories}
                                onChange={(value) => {
                                    setData({...data, eventCategories: value});
                                    setIsEditable(true);
                                }}
                                options={EVENT_CATEGORIES}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={false}
                                isDisabled={!data?.isEnabled}

                            />
                        </div>

                        <div className="function-panel-note">
                            {messages['driver_performance_cases_create_noteEventsCaped']}
                        </div>
                    </div>

                    {/* Limit Examples Section */}
                    <div className="function-panel-section">
                        <h4 className="function-panel-section-header">{messages['driver_performance_cases_create_limitExamples']}</h4>

                        <div className="function-panel-field">
                            <strong className="function-panel-info-strong">
                                {messages['driver_performance_cases_create_maximumEventsPerEventType']}
                                <span className="text-danger function-panel-info-strong-required"> *</span>
                                <Tooltip
                                    className="function-panel-tooltip"
                                    placement="bottom"
                                    title={messages['driver_performance_cases_create_maximumEventsPerEventTypeDetail']}>
                                    <Icons name="help" size="small"/>
                                </Tooltip>
                            </strong>
                            <Select
                                id="eventsLimit"
                                value={data.eventsLimit}
                                onChange={(value) => {
                                    setData({...data, eventsLimit: value});
                                    setIsEditable(true);
                                }}
                                options={LIMIT_OPTIONS_EVENT}
                                textField="label"
                                valueField="id"
                                placeholder={messages['driver_performance_cases_create_select']}
                                loading={false}
                                isDisabled={!data?.isEnabled}
                            />
                        </div>
                    </div>

                    <div className="function-panel-note-final">
                        <strong>{messages['driver_performance_cases_create_note']}: </strong>
                        <span>{messages['driver_performance_cases_create_eventNote']}</span>
                    </div>

                    <div className="function-panel-footer">
                        <Button
                            variant="primary"
                            onClick={() => formRef?.submit()}
                            isDisabled={!isEditable || (data?.isEnabled && !enableSend)}
                        >
                            {messages['save']}
                        </Button>
                    </div>
                </Form>
            </CardContent>
        </Card>
    )
}

export default CoachingEducationWidget
